/**
 * Compute the cookie `domain` so browser cookies are shared across every
 * subdomain of the current site instead of being pinned to the exact host.
 *
 * Cookies set without a `domain` default to the exact hostname (e.g.
 * `authn-web.testanswersnow.com`), so they aren't visible on sibling
 * subdomains. Returning `.<base-domain>` (e.g. `.testanswersnow.com`) makes a
 * cookie readable on any `*.testanswersnow.com` host.
 *
 * On `localhost` / bare IPs there is no registrable parent domain to scope to,
 * so we return `undefined` — callers then omit `domain` and the browser keeps
 * the cookie host-only (correct for local dev).
 *
 * @param {string} [hostname] defaults to the current location hostname
 * @returns {string | undefined} leading-dot base domain, or undefined when
 *   the host can't/shouldn't be scoped to a parent domain.
 */
export function getCookieDomain(hostname = window.location.hostname) {
  if (!hostname) return undefined;

  // localhost (and *.localhost) — no registrable parent domain.
  if (hostname === "localhost" || hostname.endsWith(".localhost")) return undefined;

  // Bare IPv4/IPv6 addresses can't carry a domain attribute.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) || hostname.includes(":")) return undefined;

  const labels = hostname.split(".");
  // Single-label host (no dot) can't be scoped to a parent.
  if (labels.length < 2) return undefined;

  // Our domains are all two-label registrable domains (getanswersnow.com,
  // testanswersnow.com), so the base is the last two labels.
  const baseDomain = labels.slice(-2).join(".");
  return `.${baseDomain}`;
}
